import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { SilentError } from 'init/silent-error';
import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/bank_accounts';

// The concrete bank-account classes the backend can point to. Narrowed from the
// legacy `z.string()` (audit rule): these are the only three subclasses, and the
// default-pointer matching relies on the value being one of them.
export const BankAccountTypeSchema = z.enum([
  'CompanyBankAccount',
  'PontoBankAccount',
  'CodaboxBankAccount',
]);

// `currency` is deliberately left as a nullable string rather than the strict
// `CurrencyCodeSchema`: it is server-managed (manual accounts default to EUR),
// never displayed or edited on this card, and an unexpected value must not fail
// the whole card's parse.
const BankAccountSchema = z.object({
  id: z.string(),
  iban: z.string(),
  bic: z.string().nullable(),
  currency: z.string().nullable(),
  type: BankAccountTypeSchema,
});

export const BankAccountsSchema = z.object({
  // Third-party synced accounts (Ponto/Codabox): read-only here.
  bankAccounts: z.array(BankAccountSchema),
  // Manually managed accounts: editable here.
  companyBankAccounts: z.array(BankAccountSchema),
  defaultBankAccountId: z.number().nullable(),
  defaultBankAccountType: BankAccountTypeSchema.nullable(),
});

export type BankAccountType = z.infer<typeof BankAccountTypeSchema>;
export type BankAccounts = z.infer<typeof BankAccountsSchema>;

export async function loadBankAccountsQueryFn(axiosInstance: Promise<AxiosInstance>) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL);

  const parseResult = BankAccountsSchema.safeParse(data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'bankAccounts');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadBankAccounts(options: QueryOptions<BankAccounts> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<BankAccounts, ApiError>({
    queryKey: ['bankAccounts'],
    queryFn: () => loadBankAccountsQueryFn(axiosInstance),
    ...options,
  });

  return addResourceNameToQueryResult<BankAccounts, ApiError, 'bankAccounts'>(
    'bankAccounts',
    queryResult,
  );
}
